import MessageType from '../../../enums/MessageType';
import Track from '../../utils/tracking';
import API from '../../api/api';
import {getDeliveryOptions} from '../deliveryOptionActions';
import getDeliveryAddressDataOrFalseIfInvalid from '../../selectors/DeliveryAddressSelectors/getDeliveryAddressDataOrFalseIfInvalid';
import fetchOrderSummary from './fetchOrderSummary';
import doMetric from '../../tracking/bigQueryMetricRecorder';
import setEditBasketValidationMessage from './setEditBasketValidationMessage';
import {updateOrderSummary, OrderSummaryUpdateReasons, setOrderSummaryUpdating, errorUpdatingOrderSummary} from '../orderSummaryActions';
import {getOrderSummary} from '../../selectors/selectors';
import {extractOrderSummaryDataAndLog} from '../../utils/orderSummaryMetrics';

function getValidationMessageTranslationKey(validationMessage) {
  const validationMessageMap = {
    'SETTING_NOT_ENABLED': 'basket.edit.errors.setting.not.enabled',
    'NOT_GUEST_CUSTOMER': 'basket.edit.errors.not.guest.customer',
    'NOT_PRODUCT_IN_OPTIONAL_BASKET_ITEMS': 'basket.edit.errors.not.product.in.optional.basket.items',
    'NOT_PRODUCT_IN_BASKET': 'basket.edit.errors.not.product.in.basket',
    'PRODUCT_IN_BASKET': 'basket.edit.errors.product.in.basket',
    'RESTRICTED_COUNTRY_FOR_PRODUCT': 'basket.edit.errors.restricted.country.for.product',
    'VALIDATION_ERROR': 'basket.edit.errors.validation.error'
  };

  return validationMessageMap[validationMessage] || 'basket.edit.errors.general.error';
}

function logOrderSummaryData(orderSummary, logKey) {
  try {
    extractOrderSummaryDataAndLog(orderSummary, logKey);
  } catch (error) {
    Track.error('Failed to log order summary data:', error);
  }
}

function handleBasketError(dispatch, errorMessage) {
  Track.error(errorMessage);
  dispatch(setEditBasketValidationMessage({
    messageTranslationKey: 'basket.edit.errors.general.error',
    messageType: MessageType.ERROR,
  }));
  dispatch(errorUpdatingOrderSummary());
}

function handleValidationError(dispatch, validationMessage, orderSummary) {
  const messageTranslationKey = getValidationMessageTranslationKey(validationMessage);
  
  dispatch(setEditBasketValidationMessage({
    messageTranslationKey,
    messageType: MessageType.ERROR,
  }));
  Track.error(`Edit basket validation error: ${validationMessage}`);
}

function handleSuccessfulUpdate(dispatch, getState) {
  dispatch(setEditBasketValidationMessage(null));
  const deliveryAddressData = getDeliveryAddressDataOrFalseIfInvalid(getState());
  
  if (deliveryAddressData) {
    return dispatch(getDeliveryOptions());
  }
  return dispatch(fetchOrderSummary());
}

export function addOrRemoveBasketItem(productId, quantity, isAddToBasket = true) {
  return (dispatch, getState) => {
    const endpoint = isAddToBasket ? '/v2/edit-basket/add' : '/v2/edit-basket/remove';
    const eventSubType = isAddToBasket ? 'addToBasket' : 'removeFromBasket';
    
    // Record metric for the basket operation
    doMetric(eventSubType, [{name: 'productId', value: productId}], [], 'impression');
    
    // Log order summary before API call
    const currentOrderSummary = getOrderSummary(getState());
    logOrderSummaryData(currentOrderSummary, `orderSummary_editBasket_${eventSubType}_before`);
    
    const payload = { productId };

    // Reset validation message and set updating state
    dispatch(setEditBasketValidationMessage(null));
    dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.EDITING_BASKET));

    return dispatch(API.doNetworkRequest(endpoint, JSON.stringify(payload)))
      .then((response) => {
        if (response && response.status === 200) {
          const orderSummary = response.msg;
          dispatch(updateOrderSummary(orderSummary));
          
          // Log order summary after successful API call
          logOrderSummaryData(orderSummary, `orderSummary_editBasket_${eventSubType}_afterAPICall`);
          
          if (orderSummary.editBasketValidationMessage) {
            handleValidationError(dispatch, orderSummary.editBasketValidationMessage, orderSummary);
          } else {
            return handleSuccessfulUpdate(dispatch, getState);
          }
        } else {
          handleBasketError(
            dispatch,
            `Error ${isAddToBasket ? 'adding to' : 'removing from'} basket - status: ${response?.status}`
          );
        }
      })
      .catch((error) => {
        handleBasketError(
          dispatch,
          `Caught error ${isAddToBasket ? 'adding to' : 'removing from'} basket: ${error}`
        );
      });
  };
}
